import type { Logger } from "@expert/logging";
import { trackAppcuesEvent } from "@expert/monitoring";
import type { GaiaPayloadConfigs } from "@expert/shared-types";
import { Group } from "@mantine/core";
import { useReactAnalytics } from "@soluto-private/eventualize-react";
import type { JSX } from "react";
import { useSendExpertAssistMessage } from "../../hooks";
import { useAutoscroll } from "../../utils";
import { QuickActionButton } from "./QuickActionButton";
import type { QuickAction } from "./types";

interface ActionGroupProps {
    actions: QuickAction[];
    setIsQuickActionsVisible: React.Dispatch<React.SetStateAction<boolean>>;
    logger: Logger;
    gaiaPayloadProps: GaiaPayloadConfigs;
    scrollRef?: React.RefObject<HTMLDivElement | null>;
    quickActionStyle?: "default" | "smartOffer";
}

export function QuickActionGroup({
    setIsQuickActionsVisible,
    actions,
    logger,
    gaiaPayloadProps,
    scrollRef,
    quickActionStyle = "default",
}: ActionGroupProps): JSX.Element {
    const sendMessage = useSendExpertAssistMessage(logger, gaiaPayloadProps);
    const { dispatcher } = useReactAnalytics();
    const { start: startAutoscroll } = useAutoscroll(scrollRef);
    const onQuickActionClick = ({
        value,
        analytics: { actionId, eventData, extraContext },
        onClick,
        isVisibleAfterClick,
    }: QuickAction) => {
        void dispatcher.dispatchBusinessEvent(`Click_${actionId}`, {
            ...eventData,
            ...extraContext,
            button: value,
        });
        trackAppcuesEvent(`Click_${actionId}`, { actionId, actionType: "QuickAction" });

        if (!isVisibleAfterClick) {
            setIsQuickActionsVisible(false);
        }

        if (onClick) {
            onClick(value);
        } else {
            sendMessage({ text: value, type: "QuickAction", source: "QuickAction" });
            startAutoscroll();
        }
    };

    const children = actions.map((action) => (
        <QuickActionButton
            key={`${action.analytics.actionId}-${action.value}`}
            name={action.analytics.actionId}
            value={action.value}
            quickActionStyle={quickActionStyle}
            loading={action.loading}
            disabled={action.disabled}
            onClick={() => onQuickActionClick(action)}
        />
    ));

    return (
        <Group gap="sm" mt="xs">
            {children}
        </Group>
    );
}
